
import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import Select from '../../components/base/Select';
import Modal from '../../components/base/Modal';

interface Template {
  id: string;
  name: string;
  description: string;
  category: string;
  type: 'text' | 'email' | 'rechnung' | 'vertrag' | 'angebot' | 'brief';
  content: string;
  variables: string[]; // Platzhalter wie {{firmenname}}, {{datum}}
  tags: string[];
  is_default: boolean;
  last_used?: string;
  usage_count: number;
  file_url?: string;
  file_name?: string;
  file_size?: number;
  file_type?: string;
  storage_path?: string;
  created_at: string;
  updated_at?: string;
}

const categories = [
  'Rechnung',
  'Angebot',
  'Offerte',
  'Vertrag',
  'Auszahlung',
  'Geschäftsbrief',
  'E-Mail Vorlage',
  'Wartungsdokument',
  'Kostenvoranschlag',
  'Mahnung',
  'Quittung',
  'Sonstiges'
];

const templateTypes = [
  { value: 'text', label: 'Text-Dokument', icon: 'ri-file-text-line' },
  { value: 'email', label: 'E-Mail Vorlage', icon: 'ri-mail-line' },
  { value: 'rechnung', label: 'Rechnung', icon: 'ri-bill-line' },
  { value: 'vertrag', label: 'Vertrag', icon: 'ri-file-paper-line' },
  { value: 'angebot', label: 'Angebot', icon: 'ri-price-tag-3-line' },
  { value: 'brief', label: 'Geschäftsbrief', icon: 'ri-mail-send-line' }
];

// Gemeinsame Variablen
const commonVariables = [
  '{{firmenname}}',
  '{{kundenname}}',
  '{{datum}}',
  '{{rechnungsnummer}}',
  '{{betrag}}',
  '{{anrede}}',
  '{{unterschrift}}',
  '{{telefon}}',
  '{{email}}',
  '{{adresse}}'
];

export default function Vorlagen() {
  // ... existing state and functions ...

  const { user } = useAuth();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterType, setFilterType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [previewVariables, setPreviewVariables] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    file: null as File | null
  });

  // Load templates from Supabase
  useEffect(() => {
    if (user) {
      loadTemplates();
    }
  }, [user]);

  // ✅ Verbesserte loadTemplates Funktion mit besserer Fehlerbehandlung
  const loadTemplates = async () => {
    try {
      console.log('🔄 Loading templates...');
      console.log('👤 Current user ID:', user?.id);
      
      if (!user?.id) {
        console.warn('⚠️ No user ID available, skipping template load');
        setTemplates([]);
        setLoading(false);
        return;
      }

      // ✅ Erweiterte Abfrage mit besserer Fehlerbehandlung
      const { data, error } = await supabase
        .from('vorlagen')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error loading templates:', error);
        console.error('❌ Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        
        // Zeige spezifische Fehlermeldung an
        if (error.message?.includes('relation "vorlagen" does not exist')) {
          alert('Die Vorlagen-Tabelle existiert nicht in der Datenbank. Bitte kontaktieren Sie den Administrator.');
        } else if (error.message?.includes('permission denied')) {
          alert('Keine Berechtigung zum Laden der Vorlagen. Bitte melden Sie sich erneut an.');
        } else {
          alert('Fehler beim Laden der Vorlagen: ' + error.message);
        }
        
        setTemplates([]);
        return;
      }

      console.log('✅ Templates loaded successfully:');
      console.log('📊 Count:', data?.length || 0);
      console.log('📋 Template IDs:', data?.map(t => t.id) || []);
      console.log('📝 Template names:', data?.map(t => t.name) || []);

      // ✅ Stelle sicher, dass data ein Array ist
      const templateArray = Array.isArray(data) ? data : [];
      setTemplates(templateArray);
      
    } catch (error) {
      console.error('❌ Unexpected error loading templates:', error);
      alert('Ein unerwarteter Fehler ist beim Laden der Vorlagen aufgetreten.');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Verbesserte Upload-Funktion für Supabase Storage "vorlagen"
  const uploadFile = async (file: File): Promise<{ 
    url: string; 
    fileName: string; 
    fileSize: number; 
    fileType: string; 
    storagePath: string;
    error?: any 
  }> => {
    try {
      console.log('🔄 Starting file upload to "vorlagen" bucket...');
      console.log('📁 File details:', { 
        name: file.name, 
        size: file.size, 
        type: file.type 
      });

      // Bereinige Dateinamen für Storage
      const fileExt = file.name.split('.').pop();
      const cleanFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const fileName = `${Date.now()}_${cleanFileName}`;
      const storagePath = `${user?.id}/${fileName}`;

      console.log('📂 Storage path:', storagePath);

      // ✅ Upload zur "vorlagen" Bucket
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('vorlagen')
        .upload(storagePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('❌ Storage upload error:', uploadError);
        
        // Prüfe ob Bucket existiert
        if (uploadError.message?.includes('Bucket not found')) {
          throw new Error('Der "vorlagen" Storage Bucket existiert nicht. Bitte wenden Sie sich an den Administrator.');
        }
        
        throw uploadError;
      }

      console.log('✅ File uploaded successfully:', uploadData);

      // ✅ Hole die öffentliche URL
      const { data: urlData } = supabase.storage
        .from('vorlagen')
        .getPublicUrl(storagePath);

      const publicUrl = urlData.publicUrl;
      console.log('🔗 Public URL generated:', publicUrl);

      return {
        url: publicUrl,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        storagePath: storagePath
      };
    } catch (error) {
      console.error('❌ Upload error:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler beim Upload';
      
      return {
        url: '',
        fileName: '',
        fileSize: 0,
        fileType: '',
        storagePath: '',
        error: { message: errorMessage }
      };
    }
  };

  const extractVariables = (content: string): string[] => {
    const variableRegex = /\{\{([^}]+)\}\}/g;
    const variables: string[] = [];
    let match;

    while ((match = variableRegex.exec(content)) !== null) {
      const variable = `{{${match[1]}}}`;
      if (!variables.includes(variable)) {
        variables.push(variable);
      }
    }

    return variables;
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData(prev => ({ ...prev, file }));
  };

  // ✅ Verbesserte Submit-Funktion mit besserer Validierung
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log('🔄 Form submitted with data:', formData);
    console.log('👤 Current user:', user);

    // ✅ Erweiterte Validierung
    if (!user) {
      alert('Sie müssen angemeldet sein, um eine Vorlage zu erstellen.');
      return;
    }

    if (!user.id) {
      alert('Benutzer-ID nicht verfügbar. Bitte melden Sie sich erneut an.');
      return;
    }

    if (!formData.name.trim()) {
      alert('Bitte geben Sie einen Namen für die Vorlage ein.');
      return;
    }

    if (!formData.category) {
      alert('Bitte wählen Sie eine Kategorie aus.');
      return;
    }

    if (!formData.description.trim()) {
      alert('Bitte geben Sie eine Beschreibung ein.');
      return;
    }

    if (!formData.file) {
      alert('Bitte wählen Sie eine Datei aus.');
      return;
    }

    setUploading(true);

    try {
      console.log('🔄 Starting file upload...');

      // ✅ Upload zur "vorlagen" Bucket
      const { url, fileName, fileSize, fileType, storagePath, error } = await uploadFile(formData.file);

      if (error) {
        console.error('❌ Error uploading file:', error);
        alert('Fehler beim Hochladen der Datei: ' + error.message);
        setUploading(false);
        return;
      }

      // ✅ Stelle sicher, dass alle Daten vorhanden sind
      if (!url || !fileName || !storagePath) {
        alert('Upload unvollständig. Bitte versuchen Sie es erneut.');
        setUploading(false);
        return;
      }

      const templateData = {
        user_id: user.id,
        name: formData.name.trim(),
        description: formData.description.trim(),
        category: formData.category,
        type: 'text' as const,
        content: '',
        variables: [],
        tags: [],
        is_default: false,
        usage_count: 0,
        file_url: url,
        file_name: fileName,
        file_size: fileSize,
        file_type: fileType,
        storage_path: storagePath
      };

      console.log('💾 Inserting template data:', templateData);

      // ✅ Verbesserte Datenbank-Einfügung
      const { data, error: dbError } = await supabase
        .from('vorlagen')
        .insert(templateData)
        .select()
        .single();

      if (dbError) {
        console.error('❌ Database error:', dbError);
        console.error('❌ Database error details:', {
          message: dbError.message,
          details: dbError.details,
          hint: dbError.hint,
          code: dbError.code
        });
        
        // Cleanup hochgeladene Datei bei DB-Fehler
        if (storagePath) {
          try {
            await supabase.storage
              .from('vorlagen')
              .remove([storagePath]);
            console.log('🗑️ Cleaned up uploaded file after DB error');
          } catch (cleanupError) {
            console.warn('⚠️ Could not cleanup file:', cleanupError);
          }
        }
        
        // Spezifische Fehlermeldungen
        if (dbError.message?.includes('duplicate key')) {
          alert('Eine Vorlage mit diesem Namen existiert bereits.');
        } else if (dbError.message?.includes('permission denied')) {
          alert('Keine Berechtigung zum Speichern. Bitte melden Sie sich erneut an.');
        } else {
          alert('Fehler beim Speichern in der Datenbank: ' + dbError.message);
        }
        
        setUploading(false);
        return;
      }

      if (!data) {
        alert('Keine Daten von der Datenbank erhalten. Bitte versuchen Sie es erneut.');
        setUploading(false);
        return;
      }

      console.log('✅ Template saved successfully:', data);

      // ✅ Aktualisiere lokalen State
      setTemplates(prev => [data, ...prev]);

      // ✅ Formular zurücksetzen
      setFormData({
        name: '',
        category: '',
        description: '',
        file: null
      });

      // ✅ File Input zurücksetzen
      const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }

      setIsModalOpen(false);
      alert('Vorlage wurde erfolgreich hinzugefügt!');

      // ✅ Zur Sicherheit Templates neu laden
      setTimeout(() => {
        console.log('🔄 Reloading templates to verify...');
        loadTemplates();
      }, 1000);
      
    } catch (error) {
      console.error('❌ Unexpected error adding template:', error);
      alert('Es ist ein unerwarteter Fehler aufgetreten. Bitte versuchen Sie es erneut.');
    } finally {
      setUploading(false);
    }
  };

  // Use template (increment usage)
  const handleUseTemplate = async (template: Template) => {
    const updatedTemplate = {
      ...template,
      usage_count: template.usage_count + 1,
      last_used: new Date().toISOString()
    };

    try {
      const { error } = await supabase
        .from('vorlagen')
        .update({
          usage_count: updatedTemplate.usage_count,
          last_used: updatedTemplate.last_used
        })
        .eq('id', template.id);

      if (error) throw error;

      setTemplates(prev => prev.map(t => (t.id === template.id ? updatedTemplate : t)));

      const initialVariables: Record<string, string> = {};
      template.variables.forEach(variable => {
        initialVariables[variable] = '';
      });
      setPreviewVariables(initialVariables);
      setSelectedTemplate(updatedTemplate);
      setIsPreviewModalOpen(true);
    } catch (error) {
      console.error('Error updating template usage:', error);
    }
  };

  // ✅ Reparierte Download-Funktion für private Storage
  const handleDownloadTemplate = async (e: React.MouseEvent, storagePath: string, fileName: string) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      console.log('⬇️ Starting secure download:', { storagePath, fileName });

      // ✅ Verwende signedURL für private Storage
      const { data: signedUrlData, error: signedUrlError } = await supabase.storage
        .from('vorlagen')
        .createSignedUrl(storagePath, 60); // 60 Sekunden gültig

      if (signedUrlError) {
        console.error('❌ Error creating signed URL:', signedUrlError);
        throw signedUrlError;
      }

      if (!signedUrlData?.signedUrl) {
        throw new Error('Keine gültige Download-URL erhalten');
      }

      console.log('✅ Signed URL created successfully');

      // Download mit signed URL
      const response = await fetch(signedUrlData.signedUrl, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status ${response.status}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(downloadUrl);

      console.log('✅ Download completed successfully');
    } catch (error) {
      console.error('❌ Error during download:', error);
      alert('Fehler beim Download: ' + (error instanceof Error ? error.message : 'Unbekannter Fehler'));
    }
  };

  // Aktualisiere auch handleFileView für sichere Anzeige
  const handleFileView = async (storagePath: string, fileName: string) => {
    try {
      const { data: signedUrlData, error: signedUrlError } = await supabase.storage
        .from('vorlagen')
        .createSignedUrl(storagePath, 300); // 5 Minuten gültig für Ansicht

      if (signedUrlError || !signedUrlData?.signedUrl) {
        console.error('Error creating signed URL for view:', signedUrlError);
        alert('Fehler beim Öffnen der Datei');
        return;
      }

      const link = document.createElement('a');
      link.href = signedUrlData.signedUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error viewing file:', error);
      alert('Fehler beim Öffnen der Datei');
    }
  };

  const generatePreview = (template: Template, variables: Record<string, string>): string => {
    let preview = template.content;

    Object.entries(variables).forEach(([variable, value]) => {
      const regex = new RegExp(variable.replace(/[{}]/g, '\\$&'), 'g');
      preview = preview.replace(regex, value || variable);
    });

    return preview;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      // optional toast can be added
    });
  };

  // ✅ Komplett überarbeitete handleDelete Funktion mit besserer Fehlerbehandlung
  const handleDelete = async (templateId: string, storagePath?: string) => {
    if (!confirm('Sind Sie sicher, dass Sie diese Vorlage löschen möchten?')) {
      return;
    }

    try {
      console.log('🗑️ Starting template deletion process...');
      console.log('📋 Template ID:', templateId);
      console.log('📁 Storage Path:', storagePath);
      console.log('👤 User ID:', user?.id);

      // 1. Erst aus der Datenbank löschen mit striktem User-Filter
      console.log('🔄 Step 1: Deleting from database...');
      const { error: dbError } = await supabase
        .from('vorlagen')
        .delete()
        .eq('id', templateId)
        .eq('user_id', user?.id); // Wichtiger Sicherheitsfilter!

      if (dbError) {
        console.error('❌ Database deletion failed:', dbError);
        alert('Fehler beim Löschen aus der Datenbank: ' + dbError.message);
        return;
      }

      console.log('✅ Step 1 completed: Template deleted from database');

      // 2. Dann sofort aus dem lokalen State entfernen
      console.log('🔄 Step 2: Updating local state...');
      setTemplates(prevTemplates => {
        const filteredTemplates = prevTemplates.filter(t => t.id !== templateId);
        console.log('📊 Templates before:', prevTemplates.length);
        console.log('📊 Templates after:', filteredTemplates.length);
        return filteredTemplates;
      });

      console.log('✅ Step 2 completed: Local state updated');

      // 3. Versuche Datei aus Storage zu löschen (nicht kritisch)
      if (storagePath) {
        console.log('🔄 Step 3: Deleting file from storage...');
        try {
          const { error: storageError } = await supabase.storage
            .from('vorlagen')
            .remove([storagePath]);

          if (storageError) {
            console.warn('⚠️ Storage deletion warning (nicht kritisch):', storageError);
          } else {
            console.log('✅ Step 3 completed: File deleted from storage');
          }
        } catch (storageErr) {
          console.warn('⚠️ Storage cleanup failed (nicht kritisch):', storageErr);
        }
      } else {
        console.log('ℹ️ Step 3 skipped: No storage path provided');
      }

      // 4. Erfolg anzeigen
      console.log('🎉 Template deletion completed successfully!');
      alert('Vorlage wurde erfolgreich gelöscht!');

      // 5. Zur Sicherheit Daten neu laden nach kurzer Verzögerung
      setTimeout(() => {
        console.log('🔄 Refreshing templates for verification...');
        loadTemplates();
      }, 1000);

    } catch (error) {
      console.error('❌ Unexpected error during deletion:', error);
      alert('Ein unerwarteter Fehler ist aufgetreten: ' + (error instanceof Error ? error.message : 'Unbekannter Fehler'));
      
      // Bei jedem Fehler - Daten neu laden
      console.log('🔄 Reloading templates due to error...');
      await loadTemplates();
    }
  };

  const getTypeInfo = (type: string) => {
    return templateTypes.find(t => t.value === type) || templateTypes[0];
  };

  const filteredTemplates = templates.filter(template => {
    const matchesCategory = !filterCategory || template.category === filterCategory;
    const matchesType = !filterType || template.type === filterType;
    const matchesSearch =
      !searchTerm ||
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesType && matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-line text-4xl text-blue-600 animate-spin mb-4"></i>
          <p className="text-gray-600">Lade Vorlagen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full overflow-x-hidden">
      <div className="w-full max-w-full overflow-x-hidden">
        <div className="bg-white rounded-lg shadow-sm border w-full overflow-x-hidden">
          <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 border-b border-gray-200">
            <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:justify-between sm:items-start">
              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 flex flex-col sm:flex-row sm:items-center">
                  <span>Vorlagen</span>
                  <span className="mt-1 sm:mt-0 sm:ml-3 text-xs sm:text-sm text-green-600">
                    <i className="ri-database-2-line mr-1"></i>
                    Supabase Storage
                  </span>
                </h1>
                <p className="text-gray-600 mt-1 text-xs sm:text-base break-words">
                  Persönliche Dokumentenvorlagen ({templates.length} Vorlagen)
                </p>
              </div>
              <button
                onClick={() => setIsModalOpen(true)}
                className="w-full sm:w-auto bg-blue-600 text-white px-4 py-3 sm:py-2 rounded-md hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center flex-shrink-0 text-sm touch-manipulation"
              >
                <i className="ri-add-line w-4 h-4 flex items-center justify-center mr-2"></i>
                Neue Vorlage
              </button>
            </div>
          </div>

          {/* Filter und Suche */}
          {templates.length > 0 && (
            <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                    <input
                      type="text"
                      placeholder="Vorlagen durchsuchen..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <select
                    value={filterCategory}
                    onChange={e => setFilterCategory(e.target.value)}
                    className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm pr-8"
                  >
                    <option value="">Alle Kategorien</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                  <select
                    value={filterType}
                    onChange={e => setFilterType(e.target.value)}
                    className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm pr-8"
                  >
                    <option value="">Alle Typen</option>
                    {templateTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          <div className="p-3 sm:p-4 lg:p-6 w-full overflow-x-hidden">
            {templates.length === 0 ? (
              <div className="text-center py-8 sm:py-12">
                <div className="text-gray-400 mb-4">
                  <i className="ri-file-word-line text-4xl sm:text-6xl w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mx-auto"></i>
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-2">
                  Keine Vorlagen vorhanden
                </h3>
                <p className="text-gray-500 text-sm sm:text-base mb-4">
                  Erstellen Sie Ihre erste Dokumentvorlage.
                </p>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer text-sm sm:text-base touch-manipulation"
                >
                  <i className="ri-add-line mr-2"></i>
                  Erste Vorlage erstellen
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {filteredTemplates.map(template => {
                  const typeInfo = getTypeInfo(template.type);
                  return (
                    <div key={template.id} className="bg-white rounded-lg shadow-sm border overflow-hidden hover:shadow-md transition-shadow">
                      <div className="p-4 sm:p-6">
                        <div className="flex items-start justify-between mb-3 sm:mb-4">
                          <div className="flex items-start space-x-3 flex-1 min-w-0">
                            <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                              <i className={`${typeInfo.icon} text-blue-600 text-lg`}></i>
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm sm:text-lg font-semibold text-gray-900 break-words">{template.name}</h3>
                              <p className="text-xs sm:text-sm text-gray-500">{template.category}</p>
                              <div className="flex flex-wrap items-center gap-1 mt-1">
                                {template.is_default && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                    <i className="ri-star-line mr-1"></i>
                                    Standard
                                  </span>
                                )}
                                {template.file_url && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    <i className="ri-file-line mr-1"></i>
                                    Datei
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4 line-clamp-2 break-words">{template.description}</p>

                        <div className="space-y-2 mb-3 sm:mb-4">
                          <div className="flex items-center justify-between text-xs sm:text-sm">
                            <span className="text-gray-600">Typ:</span>
                            <span className="font-medium break-words ml-2">{typeInfo.label}</span>
                          </div>
                          <div className="flex items-center justify-between text-xs sm:text-sm">
                            <span className="text-gray-600">Variablen:</span>
                            <span className="font-medium">{template.variables.length}</span>
                          </div>
                          <div className="flex items-center justify-between text-xs sm:text-sm">
                            <span className="text-gray-600">Verwendungen:</span>
                            <span className="font-medium">{template.usage_count}</span>
                          </div>
                          {template.last_used && (
                            <div className="flex items-center justify-between text-xs sm:text-sm">
                              <span className="text-gray-600">Zuletzt:</span>
                              <span className="font-medium">{new Date(template.last_used).toLocaleDateString('de-DE')}</span>
                            </div>
                          )}
                          {template.file_name && (
                            <div className="flex items-center justify-between text-xs sm:text-sm">
                              <span className="text-gray-600">Datei:</span>
                              <span className="font-medium break-words max-w-32">{template.file_name}</span>
                            </div>
                          )}
                        </div>

                        {template.tags && template.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-3 sm:mb-4">
                            {template.tags.slice(0, 3).map((tag, index) => (
                              <span key={index} className="px-1.5 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full">
                                {tag}
                              </span>
                            ))}
                            {template.tags.length > 3 && (
                              <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                                +{template.tags.length - 3}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Mobile: Stacked buttons */}
                        <div className="block sm:hidden space-y-2">
                          <Button size="sm" className="w-full text-xs py-3 touch-manipulation" onClick={() => handleUseTemplate(template)}>
                            <i className="ri-play-line mr-1"></i>
                            Verwenden
                          </Button>
                          <div className="flex space-x-2">
                            {template.file_url && (
                              <Button 
                                size="sm" 
                                variant="secondary" 
                                className="flex-1 text-xs py-2 touch-manipulation"
                                onClick={(e) => handleDownloadTemplate(e, template.storage_path!, template.file_name!)}
                              >
                                <i className="ri-download-line mr-1"></i>
                                Download
                              </Button>
                            )}
                            <Button size="sm" variant="secondary" className="flex-1 text-xs py-2 touch-manipulation">
                              <i className="ri-edit-line mr-1"></i>
                              Bearbeiten
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              className="flex-1 text-xs py-2 touch-manipulation"
                              onClick={() => handleDelete(template.id, template.storage_path)}
                            >
                              <i className="ri-delete-bin-line mr-1"></i>
                              Löschen
                            </Button>
                          </div>
                        </div>

                        {/* Desktop: Horizontal buttons */}
                        <div className="hidden sm:flex sm:space-x-2">
                          <Button size="sm" className="flex-1 text-xs" onClick={() => handleUseTemplate(template)}>
                            <i className="ri-play-line mr-1"></i>
                            Verwenden
                          </Button>
                          {template.file_url && (
                            <Button 
                              size="sm" 
                              variant="secondary" 
                              className="text-xs px-3 touch-manipulation"
                              onClick={(e) => handleDownloadTemplate(e, template.storage_path!, template.file_name!)}
                            >
                              <i className="ri-download-line"></i>
                            </Button>
                          )}
                          <Button size="sm" variant="secondary" className="text-xs px-3 touch-manipulation">
                            <i className="ri-edit-line"></i>
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            className="text-xs px-3 touch-manipulation"
                            onClick={() => handleDelete(template.id, template.storage_path)}
                          >
                            <i className="ri-delete-bin-line"></i>
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal für neue Vorlage */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Neue Vorlage hinzufügen" maxWidth="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Name der Vorlage"
            value={formData.name}
            onChange={e => handleInputChange('name', e.target.value)}
            placeholder="z.B. Standard Rechnung, Wartungsvertrag..."
            required
          />

          <Select
            label="Kategorie"
            options={categories.map(cat => ({ value: cat, label: cat }))}
            value={formData.category}
            onChange={value => handleInputChange('category', value)}
            placeholder="Kategorie auswählen..."
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Beschreibung</label>
            <textarea
              value={formData.description}
              onChange={e => handleInputChange('description', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
              rows={3}
              placeholder="Kurze Beschreibung der Vorlage..."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Datei anhängen</label>
            <input
              type="file"
              onChange={handleFileChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              accept=".pdf,.doc,.docx,.txt,.xlsx,.xls,.ppt,.pptx"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Unterstützte Formate: PDF, Word, Excel, PowerPoint, Text (max. 10MB)
            </p>
          </div>

          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
            <Button type="submit" className="w-full sm:flex-1 py-3 sm:py-2 text-sm sm:text-base touch-manipulation" disabled={uploading}>
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Wird hinzugefügt...
                </>
              ) : (
                'Vorlage hinzufügen'
              )}
            </Button>
            <Button type="button" variant="secondary" className="w-full sm:flex-1 py-3 sm:py-2 text-sm sm:text-base touch-manipulation" onClick={() => setIsModalOpen(false)} disabled={uploading}>
              Abbrechen
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal für Vorlagen-Verwendung */}
      <Modal isOpen={isPreviewModalOpen} onClose={() => setIsPreviewModalOpen(false)} title="Vorlage verwenden" maxWidth="3xl">
        {selectedTemplate && (
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-1 break-words">{selectedTemplate.name}</h3>
              <p className="text-sm text-gray-600 break-words">{selectedTemplate.description}</p>
              {selectedTemplate.file_url && (
                <div className="mt-2">
                  <button
                    onClick={() => handleFileView(selectedTemplate.storage_path!, selectedTemplate.file_name!)}
                    className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 cursor-pointer touch-manipulation"
                  >
                    <i className="ri-download-line mr-1"></i>
                    {selectedTemplate.file_name} herunterladen
                  </button>
                </div>
              )}
            </div>

            {selectedTemplate.variables.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Variablen ausfüllen:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedTemplate.variables.map(variable => (
                    <Input
                      key={variable}
                      label={variable.replace(/[{}]/g, '')}
                      value={previewVariables[variable] || ''}
                      onChange={e =>
                        setPreviewVariables(prev => ({
                          ...prev,
                          [variable]: e.target.value
                        }))
                      }
                      placeholder={`Wert für ${variable}`}
                    />
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 space-y-2 sm:space-y-0">
                <h4 className="font-medium text-gray-900">Vorschau:</h4>
                <Button size="sm" variant="secondary" onClick={() => copyToClipboard(generatePreview(selectedTemplate, previewVariables))} className="touch-manipulation">
                  <i className="ri-clipboard-line mr-1"></i>
                  Kopieren
                </Button>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border max-h-64 overflow-y-auto">
                <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans break-words">
                  {generatePreview(selectedTemplate, previewVariables)}
                </pre>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-2 pt-4">
              <Button
                onClick={() => setIsPreviewModalOpen(false)}
                className="py-3 sm:py-2 text-sm sm:text-base touch-manipulation"
              >
                Schließen
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
